import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';

import RightModalLayout from '../../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';
import FormProviderMC from '../../components/FormProviderMC';
import InputMC from '../../components/InputMC';
import DropdownMC from '../../components/DropdownMC';
import CustomersModelMC from "../../models/CustomersModelMc";
import { DOCUMENT_TYPES } from '../../../constants/AppConstants';
import { getCountryOptions } from '../../../services/countryService';
import CustomIconMC from '../../components/CustomIconMC';
import { visiblePhoneNumber } from '../../../util/phoneNumber';

const existsPhone = async (customer) => {
  try {
    const textSearch = customer.phone && customer.phone.length > 0 ? `${customer.phone.replace(/^(0+)/g, '')}` : customer.email;
    const response = await CustomersModelMC.search({ text: textSearch });
    return response?.customers?.length > 0;
  } catch (error) {
    console.error(error);
  }
  return false;
};

const ManageClientForOrderModal = ({ customer, action, onSaveSuccess }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('Orders');
  const { t: tCommon } = useTranslation('Common');
  const { currentRetailerUser } = useSelector((reduxState) => reduxState.mainReducer);

  const [saving, setSaving] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
  const [rootError, setRootError] = useState('');
  const [prevPhone, setPrevPhone] = useState('');

  const maskPhoneToDisplay = (masked) => {
    if (action === 'new') return customer?.phone;
    // Always mask phone if user doesn't have permission, regardless of ws_active
    return masked ? visiblePhoneNumber(customer?.phone, currentRetailerUser, 3) : customer?.phone;
  };

  const schema = object().shape({
    first_name: string().required(tCommon('form.validations.required')),
    last_name: string().required(tCommon('form.validations.required')),
    country_id: string().required(tCommon('form.validations.required')),
    phone: string()
      .nullable()
      .test(
        'is-valid-phone',
        tCommon('form.validations.phone.validation_format'),
        (value, context) => {
          const { email } = context.parent;
          if (!value && !email) return false;
          if (!value) return true;

          if (/[xX*]/.test(value)) return true;

          return /^\+?\d+$/.test(value);
        }
      ),
    email: string()
      .nullable()
      .test(
        'is-valid-email',
        tCommon('form.validations.email.validation_format'),
        (value) => !value || (typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      )
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      email: customer?.email || '',
      country_id: customer?.country_id || ENV.COUNTRY_CODE,
      phone: maskPhoneToDisplay(!currentRetailerUser?.can_view_phone_numbers),
      id_type: customer?.id_type || '',
      id_number: customer?.id_number || '',
      web_id_in_mercately: customer?.web_id_in_mercately || ''
    }
  });

  useEffect(() => {
    setPrevPhone(customer?.phone);
    setCountryOptions(getCountryOptions().map((country) => ({
      id: country.code,
      ...country
    })));

    setDocumentTypeOptions(DOCUMENT_TYPES.map((type) => ({ id: type.value, ...type })));
  }, []);

  const filterOption = (searchTerm) => countryOptions.filter((option) => option.name_es?.toLowerCase().includes(searchTerm.toLowerCase()) || option.name_en?.toLowerCase().includes(searchTerm.toLowerCase()));

  const closeSidebar = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  const submitAction = async (formData) => {
    const {
      first_name, last_name, email, country_id, phone, id_type, id_number, web_id_in_mercately
    } = formData;

    if (!web_id_in_mercately && await existsPhone(formData)) {
      setRootError(t('customer_modal.root_error'));
      return;
    }

    const new_data = {
      customer: {
        first_name,
        last_name,
        email,
        country_id,
        phone: (/[xX*]/.test(phone)) ? prevPhone : phone,
        id_type,
        id_number
      }
    };

    setSaving(true);

    try {
      const isNew = !web_id_in_mercately;
      if (!isNew) new_data.id = web_id_in_mercately;

      const method = isNew
        ? CustomersModelMC.createFromRetailer
        : CustomersModelMC.updateFromRetailer;

      const response = await method(new_data);
      onSaveSuccess(response?.customer);
    } catch (error) {
      showtoast(error.message);
      setSaving(false);
    }

    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={action === 'new' ? t('customer_modal.new_title') : t('customer_modal.edit_title')}
        submitAction={methods.handleSubmit(submitAction)}
        submitLabel={tCommon('actions.save')}
        submitDisabled={saving}
        cancelAction={closeSidebar}
        closeAction={closeSidebar}
        cancelLabel={tCommon('actions.cancel')}
        footerVariant="HORIZONTAL"
        footerClassName="tw-border-none"
      >
        <FormProviderMC
          methods={methods}
          className="tw-space-y-4"
        >
          {rootError && (
            <div className="tw-flex tw-p-4 tw-rounded-xl tw-bg-red-50 tw-border-red-100 tw-text-red-500 tw-text-sm tw-gap-1">
              <CustomIconMC name="info-outline" size="base" />
              <span className="tw-text-xs tw-text-red-500 tw-font-medium">{rootError}</span>
            </div>
          )}
          <InputMC
            id="email"
            name="email"
            label={tCommon('form.email')}
            className="tw-resize-none"
            useFloatingLabel
          />
          <InputMC
            id="first_name"
            name="first_name"
            label={tCommon('form.first_name')}
            className="tw-resize-none"
            useFloatingLabel
          />
          <InputMC
            id="last_name"
            name="last_name"
            label={tCommon('form.last_name')}
            className="tw-resize-none"
            useFloatingLabel
          />
          <div className="tw-flex tw-gap-2">
            <DropdownMC
              search
              useFloatingLabel
              id="country_id"
              name="country_id"
              label={tCommon('form.country')}
              options={countryOptions}
              defaultValue={methods.getValues('country_id')}
              filterOption={filterOption}
              className="!tw-w-[55%]"
              disabled={customer?.ws_active}
            />
            <div className="tw-w-full">
              <InputMC
                id="phone"
                name="phone"
                label={tCommon('form.phone_number')}
                className="tw-resize-none !tw-w-full"
                useFloatingLabel
                disabled={customer?.ws_active || (action !== 'new' && !currentRetailerUser?.can_view_phone_numbers)}
                placeholder="999999999"
              />
            </div>
          </div>
          <div className="tw-flex tw-gap-2">
            <DropdownMC
              id="id_type"
              name="id_type"
              label={tCommon('form.document_type')}
              className="!tw-w-[55%]"
              options={documentTypeOptions}
              defaultValue={methods.getValues('id_type')}
              useFloatingLabel
              placeholder={tCommon('form.placeholders.select')}
            />
            <div className="tw-w-full">
              <InputMC
                id="id_number"
                name="id_number"
                label={tCommon('form.identification')}
                className="tw-resize-none"
                useFloatingLabel
              />
            </div>
          </div>
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default ManageClientForOrderModal;
